import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faSpinner,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { of } from "rxjs";
import { catchError } from "rxjs/operators";

import { TagInputComponent } from "../tag-input/tag-input.component";
import { JobPostingService } from "../../../api/services/job-posting.service";
import { AlertService } from "../../services/alert.service";
import { LocaleService } from "../../services/locale.service";
import { Skill } from "../../../api/models/skill";

/**
 * Formly field for required skills on a job posting.
 *
 * Renders an editable chip list (via `app-tag-input`) plus a CTA that calls
 * `POST /jobposting/skillset` to extract ESCO-derived skills from the
 * vacancy description (Mistral on the BE). Extraction is **additive** —
 * recruiter edits are never overwritten.
 *
 * BE persistence is a single string field (`JobPosting.requiredSkills`).
 * Chips are kept in a local signal for editing and serialised back to a
 * newline-separated string on every change, so Formly's `formControl.value`
 * matches the BE shape without any extra plumbing in `saveJobPosting`.
 *
 * @see User story #18085
 *
 * Usage:
 * ```ts
 * {
 *   key: 'requiredSkills',
 *   type: 'skills-input',
 *   props: {
 *     label: _('Required skills'),
 *     // The form key whose value is sent to the BE for extraction.
 *     descriptionField: 'description',
 *   },
 * }
 * ```
 */
@Component({
  selector: "formly-skills-input",
  standalone: true,
  imports: [FormsModule, TranslateModule, FontAwesomeModule, TagInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="flex flex-col gap-2">
      @if (props.label) {
        <span class="font-medium text-[14px]">{{ props.label | translate }}</span>
      }
      <app-tag-input
        [ngModel]="chips()"
        (ngModelChange)="onChipsChange($event)"
        [placeholder]="props['placeholder'] || 'Add a required skill'"
        [allowCreate]="true">
      </app-tag-input>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-mty-primary text-mty-primary text-[13px] font-medium hover:bg-mty-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isExtracting() || !hasDescription()"
          (click)="generate()">
          <fa-icon
            [icon]="isExtracting() ? faSpinner : faWandMagicSparkles"
            [animation]="isExtracting() ? 'spin' : undefined">
          </fa-icon>
          @if (isExtracting()) {
            {{ 'Generating skills...' | translate }}
          } @else {
            {{ 'Generate skills from description' | translate }}
          }
        </button>
      </div>
      <span class="text-[12px] text-black/55">
        {{ 'Skills are extracted from the description and can be edited.' | translate }}
      </span>
    </div>
  `,
})
export class SkillsInputFieldType
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly jobPostingService = inject(JobPostingService);
  private readonly alertService = inject(AlertService);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly faWandMagicSparkles = faWandMagicSparkles;
  protected readonly faSpinner = faSpinner;

  readonly chips = signal<string[]>([]);
  readonly isExtracting = signal(false);
  /** AI-returned descriptions keyed by skill name. Future-use for tooltips. */
  readonly skillDescriptions = signal<Record<string, string>>({});

  ngOnInit(): void {
    // Hydrate chips from the BE string on first render. We mirror straight
    // off `formControl.value` rather than `model[key]` so deeply-nested keys
    // (e.g. `requiredSkills` under a fieldGroup) keep working without extra
    // path-walking.
    this.chips.set(this.parse(this.formControl?.value as string | null));
  }

  /** Newline-separated → array, drop blanks. */
  private parse(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  /** Array → newline-separated. Empty array becomes null so the BE doesn't
   * receive an empty string that some `[Required]` validators may reject. */
  private serialise(chips: string[]): string | null {
    return chips.length === 0 ? null : chips.join("\n");
  }

  onChipsChange(chips: string[]): void {
    this.chips.set(chips);
    if (this.formControl) {
      this.formControl.setValue(this.serialise(chips));
      this.formControl.markAsDirty();
    }
  }

  /** True when the field referenced by `props.descriptionField` has any
   * non-whitespace content. Drives the disabled state on the CTA. */
  hasDescription(): boolean {
    return !!this.readDescription();
  }

  /**
   * Reads the source description from the surrounding form model. Default
   * to `description` to match the convention in `buildJobPostingFields`;
   * callers can override via `props.descriptionField` if the field has
   * been renamed.
   */
  private readDescription(): string {
    const key = (this.props["descriptionField"] as string) || "description";
    const model = (this.model ?? {}) as Record<string, unknown>;
    const raw = model[key];
    if (typeof raw !== "string") return "";
    // Description fields in this form are CKEditor HTML — strip tags before
    // sending to the extraction endpoint so the Mistral prompt isn't
    // diluted by markup.
    return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  generate(): void {
    const description = this.readDescription();
    if (!description || this.isExtracting()) return;

    this.isExtracting.set(true);

    this.jobPostingService
      .extractSkillSet({
        body: {
          jobPostingDescription: description,
          targetLanguage: this.localeService.getCurrentLanguage(),
        },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          this.alertService.errorWithReport(err, "jobPosting:extractSkillSet", {
            descriptionLength: description.length,
            targetLanguage: this.localeService.getCurrentLanguage(),
          });
          return of({ requiredSkills: [] as Skill[] });
        })
      )
      .subscribe((result) => {
        this.isExtracting.set(false);

        const extracted = result?.requiredSkills ?? [];
        if (extracted.length === 0) {
          this.alertService.success("No additional skills found");
          return;
        }

        const known = new Set(this.chips());
        const merged = [...this.chips()];
        const descriptions = { ...this.skillDescriptions() };
        let added = 0;

        for (const skill of extracted) {
          const name = skill?.name?.trim();
          if (!name || known.has(name)) continue;
          known.add(name);
          merged.push(name);
          added++;
          if (skill.description) descriptions[name] = skill.description;
        }

        this.skillDescriptions.set(descriptions);
        if (added > 0) this.onChipsChange(merged);
      });
  }
}

